import * as React from 'react';
import { Form, DatePickerRange, TextInput, CustomSubmitButton, Checkbox } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { IRiskPotReportSearchParams } from '../../../models/IRiskPotReport';
import { RiskPotReportSearchApi } from '../../../api/RiskPotReportSearchApi';
import { CareProviderDropdown } from '../../CareProviderDropdown/CareProviderDropdown';
import { ICareProvider } from '../../../models/ICareProvider';
import { UserContext, isInRole } from '../../../contexts/UserContext';
import { LIBRETTOADMIN, BEDÖMNINGSTANDLÄKARE, ALLMÄNTANDVÅRDBAS, VÅRDANSVARIG } from '../../../utils/RoleConstants';

interface IRiskPotReportSearchForm {
    searchRiskPotReports: (data: IRiskPotReportSearchParams) => void;
    preSetSearchParams?: IRiskPotReportSearchParams;
}

export const RiskPotReportSearchForm: React.FunctionComponent<IRiskPotReportSearchForm> = ({ searchRiskPotReports, preSetSearchParams }) => {
    const auth = useAuth();
    const api = new RiskPotReportSearchApi(auth);
    const [isFetchingUmbrellaOrganisations, setIsFetchingUmbrellaOrganisations] = React.useState<boolean>(false);
    const [isFetchingCareProviders, setIsFetchingCareProviders] = React.useState<boolean>(false);
    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>();
    const [umbrellaOrganisations, setUmbrellaOrganisations] = React.useState<ICareProvider[]>([]);
    const [selectedUmbrellaCareProviderId, setSelectedUmbrellaCareProviderId] = React.useState<number>();
    const [resetCareProviderValue, doResetCareProviderValue] = React.useState<boolean>(false);
    const [isCareProviderSelected, setIsCareProviderSelected] = React.useState<boolean>(false);

    const userInfo = React.useContext(UserContext);
    const [onlyShowRiskPotReportsWithUnreadPreAssessmentDecision, setOnlyShowRiskPotReportsWithUnreadPreAssessmentDecision] = React.useState<boolean>()
    const hasRoleSystemAdmin = isInRole(LIBRETTOADMIN, userInfo.roles ? userInfo.roles : []);
    const hasRoleBedomningstandlakare = isInRole(BEDÖMNINGSTANDLÄKARE, userInfo.roles ? userInfo.roles : []);
    const hasRoleAllmantandvard = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);
    const abortController = new AbortController();

    React.useEffect(() => {
        getUmbrellaOrganisations();

        if (preSetSearchParams?.onlyShowRiskPotReportsWithUnreadPreAssessmentDecision) {
            setOnlyShowRiskPotReportsWithUnreadPreAssessmentDecision(true);
        }

        return () => {
            abortController.abort();
        }
    }, [preSetSearchParams])

    const getUmbrellaOrganisations = () => {
        setIsFetchingUmbrellaOrganisations(true);
        api.getUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaOrganisationIdToUse;
            if (preSetSearchParams?.umbrellaCareProviderId) {
                umbrellaOrganisationIdToUse = parseInt(preSetSearchParams.umbrellaCareProviderId.toString());
                setSelectedUmbrellaCareProviderId(umbrellaOrganisationIdToUse)
                getCareProvidersByUmbrellaCareProviderId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            }
            else if (!hasRoleSystemAdmin && !hasRoleBedomningstandlakare) {
                umbrellaOrganisationIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedUmbrellaCareProviderId(umbrellaOrganisations[0].careProviderId);
                getCareProvidersByUmbrellaCareProviderId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            }

            setUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingUmbrellaOrganisations(false);
        });
    }

    const getCareProvidersByUmbrellaCareProviderId = (umbrellaCareProviderId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingCareProviders(true);

        api.getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderId, (careProviders) => {
            if (careProviders.length !== 0) {
                setCareProviders(careProviders);
            } else {
                setCareProviders(undefined);
            }
            setIsFetchingCareProviders(false);
        }, undefined, () => { });
    }

    const onUmbrellaCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (hasRoleSystemAdmin || hasRoleBedomningstandlakare) {
            if (selectedCareProvider && selectedCareProvider !== "") {
                getCareProvidersByUmbrellaCareProviderId(parseInt(selectedCareProvider));
                setSelectedUmbrellaCareProviderId(parseInt(selectedCareProvider));
            }
            else {
                setSelectedUmbrellaCareProviderId(undefined);
                setCareProviders([]);
            }
            doResetCareProviderValue(prev => !prev);
        }
    }

    const onCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (selectedCareProvider) {
            setIsCareProviderSelected(true);
        } else {
            setIsCareProviderSelected(false);
        }
    }

    const getUmbrellaCareProviderDefaultValue = () => {
        if (umbrellaOrganisations && preSetSearchParams?.umbrellaCareProviderId) {
            return preSetSearchParams.umbrellaCareProviderId.toString();
        }

        if (selectedUmbrellaCareProviderId) {
            return selectedUmbrellaCareProviderId.toString();
        }

        if (umbrellaOrganisations?.length === 1) {
            return umbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getCareProviderDefaultValue = () => {
        if (careProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.careProviderId
                ? parseInt(preSetSearchParams.careProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? careProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (careProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.careProviderId?.toString();
            }

            if (careProviders && careProviders.length === 1 && careProviders[0] !== undefined) {
                return careProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    return (
        <section>
            <Form onSubmit={searchRiskPotReports}>
                <div className="row">
                    <div className="col-6">
                        <DatePickerRange label="Datum" name="dateFrom" nameSecondary="dateTo" inlineLabel
                            value={preSetSearchParams?.dateFrom ? new Date(preSetSearchParams.dateFrom) : undefined}
                            valueSecondary={preSetSearchParams?.dateTo ? new Date(preSetSearchParams.dateTo) : undefined}/>
                        <TextInput name="riskPotReportNumber" label="Ärendenummer" inlineLabel defaultValue={preSetSearchParams?.riskPotReportNumber}/>
                    </div>
                    <div className="col-6">
                        <CareProviderDropdown
                            name="umbrellaCareProviderId"
                            label="Vårdgivare"
                            isLoading={isFetchingUmbrellaOrganisations}
                            careProviders={umbrellaOrganisations}
                            onValueChange={onUmbrellaCareProviderChange}
                            defaultValue={getUmbrellaCareProviderDefaultValue()}
                            disabled={!hasRoleSystemAdmin && !hasRoleSystemAdmin}
                            useFixedListItemHeight
                            showOrgNum
                            placeholder="Välj vårdgivare"
                            isClearable
                        />
                        <CareProviderDropdown
                            name="careProviderId"
                            label="Mottagning"
                            isLoading={isFetchingCareProviders}
                            onValueChange={onCareProviderChange}
                            careProviders={careProviders ?? []}
                            defaultValue={getCareProviderDefaultValue()}
                            disabled={!(hasRoleParaply || hasRoleSystemAdmin || hasRoleBedomningstandlakare)}
                            useFixedListItemHeight
                            showClinicId
                            isClearable
                            resetValue={resetCareProviderValue}
                        />
                        {
                            hasRoleAllmantandvard ?
                                <Checkbox name="onlyShowRiskPotReportsWithUnreadPreAssessmentDecision" id="onlyShowRiskPotReportsWithUnreadPreAssessmentDecision" labelCol={6} inputCol={6} label="Visa endast riskpottsärenden med oläst förhandsbedömningsbeslut" value="true"
                                    checked={onlyShowRiskPotReportsWithUnreadPreAssessmentDecision} onChange={(changedValue) => setOnlyShowRiskPotReportsWithUnreadPreAssessmentDecision(changedValue)} />
                                : null
                        }
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <CustomSubmitButton>Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
            </Form>
        </section>
    )
}